/** Connection setup, the passphrase prompt, and settings. */
import { useState } from "react";

import { Card, Field, Row, Rows, Segmented, Toggle, buttonClass, inputClass } from "../components/ui.js";
import { FEATURES } from "../features.js";
import { loadThresholds, permission, requestPermission, saveThresholds } from "../alerts.js";
import type { Permission, Thresholds } from "../alerts.js";
import { THEME_OPTIONS, loadTheme, saveTheme } from "../theme.js";
import type { ThemeChoice } from "../theme.js";
import { useEffect } from "react";
import { Wordmark } from "../components/Logo.js";
import { PinSetup } from "./Lock.js";
import { clearCompany } from "../sync/db.js";
import { useApp } from "../state.js";

export function Setup() {
  const { connection, saveConnection } = useApp();
  const [baseUrl, setBaseUrl] = useState(connection?.baseUrl ?? "");
  const [readToken, setReadToken] = useState(connection?.readToken ?? "");
  const [companyId, setCompanyId] = useState(connection?.companyId ?? "");
  const [busy, setBusy] = useState(false);

  const ready = baseUrl.trim() !== "" && readToken.trim() !== "" && companyId.trim() !== "";

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <div className="pt-6 pb-2">
        <Wordmark />
      </div>
      <h1 className="text-xl font-semibold">Connect</h1>
      <p className="text-sm text-ink-2">
        These identify the Worker holding your encrypted snapshots. They are not the passphrase and
        cannot decrypt anything on their own.
      </p>

      <Card>
        <div className="space-y-4">
          <Field label="Worker URL" hint="https://<name>.<sub>.workers.dev">
            <input
              className={inputClass}
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="https://tally-pocket.example.workers.dev"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
          </Field>
          <Field label="Read token" hint="The Worker's READ_TOKEN. It cannot overwrite your books.">
            <input
              className={inputClass}
              type="password"
              autoCapitalize="none"
              autoCorrect="off"
              value={readToken}
              onChange={(e) => setReadToken(e.target.value)}
            />
          </Field>
          <Field label="Company" hint="The slug from the agent's config.json, e.g. acme">
            <input
              className={inputClass}
              autoCapitalize="none"
              autoCorrect="off"
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            />
          </Field>
          <button
            type="button"
            className={buttonClass}
            disabled={!ready || busy}
            onClick={() => {
              setBusy(true);
              void saveConnection({ baseUrl, readToken, companyId }).finally(() => setBusy(false));
            }}
          >
            Save
          </button>
        </div>
      </Card>
    </div>
  );
}

export function Unlock() {
  const { unlock, message, syncing, connection } = useApp();
  const [passphrase, setPassphrase] = useState("");

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-4">
      <Wordmark size={30} />
      <h1 className="text-xl font-semibold">Unlock {connection?.companyId}</h1>
      <p className="text-sm text-ink-2">
        The passphrase set on the laptop agent. It is held in memory for this session only, and is
        never written to this device.
      </p>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (passphrase) void unlock(passphrase);
        }}
      >
        <input
          className={inputClass}
          type="password"
          autoFocus
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="Passphrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
        />
        <button type="submit" className={buttonClass} disabled={!passphrase || syncing}>
          {syncing ? "Unlocking…" : "Unlock"}
        </button>
      </form>

      {message && <p className="text-sm text-bad">{message}</p>}
    </div>
  );
}

export function Settings() {
  const {
    connection,
    saveConnection,
    snapshot,
    generatedAt,
    fresh,
    lock,
    flags,
    setFlag,
    lockInfo,
    sessionPassphrase,
    refreshLockInfo,
    removeLock,
    companies,
    switchCompany,
    forgetCompany,
  } = useApp();
  const [settingPin, setSettingPin] = useState(false);
  const [notifications, setNotifications] = useState<Permission>(() => permission());
  const [theme, setTheme] = useState<ThemeChoice>(() => loadTheme());
  const [limits, setLimits] = useState<Thresholds>({});
  const [limitsSaved, setLimitsSaved] = useState(false);

  useEffect(() => {
    void loadThresholds().then(setLimits);
  }, []);
  const [baseUrl, setBaseUrl] = useState(connection?.baseUrl ?? "");
  const [readToken, setReadToken] = useState(connection?.readToken ?? "");
  const [companyId, setCompanyId] = useState(connection?.companyId ?? "");
  const [cleared, setCleared] = useState(false);

  return (
    <div className="space-y-4">
      <Card title="Appearance">
        <Segmented
          value={theme}
          onChange={(next) => {
            setTheme(next);
            saveTheme(next);
          }}
          options={THEME_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
        <p className="mt-2 text-xs text-ink-3">
          {THEME_OPTIONS.find((o) => o.value === theme)?.hint}
          {theme === "system" && " — it will change with the phone, including on a schedule."}
        </p>
      </Card>

      <Card title="Features" flush>
        <Rows>
          {FEATURES.map((feature) => (
            <Toggle
              key={feature.id}
              title={feature.title}
              blurb={feature.blurb}
              risk={
                // Say what the browser has already decided. A user who blocked
                // notifications once will otherwise flip this switch on and
                // conclude the app is broken when nothing ever arrives.
                feature.id === "alerts" && notifications === "denied"
                  ? "This browser is blocking notifications for Ledgerly Go. Turn them on in the site settings first — the switch cannot override it."
                  : feature.id === "alerts" && notifications === "unsupported"
                    ? "This browser does not support notifications."
                    : feature.risk
              }
              checked={flags[feature.id]}
              onChange={(on) => {
                void (async () => {
                  if (feature.id === "alerts" && on) {
                    // Ask ONLY on an explicit opt-in. A permission prompt on
                    // first app open is how you earn a permanent block.
                    setNotifications(await requestPermission());
                  }
                  await setFlag(feature.id, on);
                })();
              }}
            />
          ))}
        </Rows>
      </Card>

      {companies.length > 1 && (
        <Card title="Companies" flush>
          <Rows>
            {companies.map((entry) => (
              <Row
                key={entry.companyId}
                title={entry.companyId}
                sub={
                  entry.companyId === connection?.companyId
                    ? "showing now"
                    : new URL(entry.baseUrl).hostname
                }
                right={
                  entry.companyId === connection?.companyId ? (
                    <span className="text-xs font-semibold text-accent">Active</span>
                  ) : undefined
                }
                onClick={
                  entry.companyId === connection?.companyId
                    ? undefined
                    : () => void switchCompany(entry.companyId)
                }
              />
            ))}
          </Rows>
        </Card>
      )}

      {companies.length > 1 && connection && (
        <Card>
          <button
            type="button"
            className="w-full rounded-xl bg-surface-2 px-4 py-3 text-[15px] font-medium text-ink-2 transition-transform active:scale-[0.99]"
            onClick={() => void forgetCompany(connection.companyId)}
          >
            Remove “{connection.companyId}” from this phone
          </button>
          <p className="mt-1 text-xs text-ink-3">
            Removes it from the switcher. The downloaded copy stays until you delete it below.
          </p>
        </Card>
      )}

      <Card title="Snapshot">
        <dl className="space-y-1 text-sm">
          <Line label="Company" value={snapshot?.company.name ?? "—"} />
          <Line label="Generated" value={generatedAt ?? "—"} />
          <Line label="Age" value={fresh?.label ?? "—"} />
          <Line label="Vouchers" value={String(snapshot?.vouchers.length ?? 0)} />
          <Line label="Ledgers" value={String(snapshot?.ledgers.length ?? 0)} />
          <Line label="Schema" value={String(snapshot?.schema ?? "—")} />
        </dl>
      </Card>

      <Card title="Connection">
        <div className="space-y-4">
          <Field label="Worker URL">
            <input className={inputClass} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
          </Field>
          <Field label="Read token">
            <input
              className={inputClass}
              type="password"
              value={readToken}
              onChange={(e) => setReadToken(e.target.value)}
            />
          </Field>
          <Field label="Company">
            <input className={inputClass} value={companyId} onChange={(e) => setCompanyId(e.target.value)} />
          </Field>
          <button
            type="button"
            className={buttonClass}
            onClick={() => void saveConnection({ baseUrl, readToken, companyId })}
          >
            {companyId && companyId !== connection?.companyId ? "Add company" : "Save"}
          </button>
          <p className="text-xs text-ink-3">
            Changing the company name here adds a second company rather than renaming this one.
            Each keeps its own downloaded copy.
          </p>
        </div>
      </Card>

      {flags.alerts && (
        <Card title="Alert limits">
          <p className="mb-4 text-sm text-ink-2">
            Leave a box empty for no limit. Nothing here has a default — a number the software
            guessed would fire on an ordinary day and teach you to ignore alerts.
          </p>

          <div className="space-y-4">
            <Field label="Total receivable above" hint="₹, across all customers">
              <input
                className={inputClass}
                inputMode="numeric"
                value={limits.receivableCeiling ?? ""}
                onChange={(e) =>
                  setLimits({ ...limits, receivableCeiling: numberOrUndefined(e.target.value) })
                }
              />
            </Field>
            <Field label="Any one customer owes above" hint="₹, a concentration warning">
              <input
                className={inputClass}
                inputMode="numeric"
                value={limits.partyCeiling ?? ""}
                onChange={(e) =>
                  setLimits({ ...limits, partyCeiling: numberOrUndefined(e.target.value) })
                }
              />
            </Field>
            <Field label="Customers gone quiet above" hint="count, not money">
              <input
                className={inputClass}
                inputMode="numeric"
                value={limits.dormantCeiling ?? ""}
                onChange={(e) =>
                  setLimits({ ...limits, dormantCeiling: numberOrUndefined(e.target.value) })
                }
              />
            </Field>

            <button
              type="button"
              className={buttonClass}
              onClick={() => {
                void saveThresholds(limits).then(() => {
                  setLimitsSaved(true);
                  setTimeout(() => setLimitsSaved(false), 2500);
                });
              }}
            >
              Save limits
            </button>
            {limitsSaved && <p className="text-xs text-good">Saved.</p>}
          </div>
        </Card>
      )}

      {flags.appLock && (
        <Card title="PIN">
          {settingPin && sessionPassphrase ? (
            <PinSetup
              passphrase={sessionPassphrase}
              onDone={() => {
                setSettingPin(false);
                void refreshLockInfo();
              }}
              onCancel={() => setSettingPin(false)}
            />
          ) : lockInfo.kind === "set" ? (
            <div className="space-y-3">
              <p className="text-sm text-ink-2">
                A PIN is set on this phone. Your passphrase is stored here encrypted with it.
              </p>
              <button
                type="button"
                className="w-full rounded-xl bg-bad-wash px-4 py-3 text-[15px] font-semibold text-bad transition-transform active:scale-[0.99]"
                onClick={() => void removeLock()}
              >
                Remove the PIN
              </button>
            </div>
          ) : !sessionPassphrase ? (
            // Nothing to wrap. Reached only if the session was locked while
            // this screen stayed mounted.
            <p className="text-sm text-ink-3">Unlock with your passphrase first.</p>
          ) : (
            <button type="button" className={buttonClass} onClick={() => setSettingPin(true)}>
              Set a PIN
            </button>
          )}
        </Card>
      )}

      <Card title="This device">
        <div className="space-y-3">
          <button type="button" className={buttonClass} onClick={lock}>
            Lock now
          </button>
          <div>
            <button
              type="button"
              className="w-full rounded-xl bg-bad-wash px-4 py-3 text-[15px] font-semibold text-bad transition-transform active:scale-[0.99]"
              onClick={() => {
                if (!connection) return;
                void clearCompany(connection.companyId).then(() => {
                  setCleared(true);
                  lock();
                });
              }}
            >
              Delete the local copy
            </button>
            <p className="mt-1 text-xs text-ink-3">
              Removes the decrypted snapshot from this phone. The next unlock downloads it again.
            </p>
            {cleared && <p className="mt-1 text-xs text-good">Deleted.</p>}
          </div>
        </div>
      </Card>
    </div>
  );
}

/** Empty or nonsense means "no limit", never zero — see saveThresholds. */
function numberOrUndefined(raw: string): number | undefined {
  const value = Number(raw.replace(/[^0-9.]/g, ""));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-3">{label}</dt>
      <dd className="truncate text-ink">{value}</dd>
    </div>
  );
}
